import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import api, { formatApiError, fileUrl, BACKEND_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Send, Lock, MessageCircle, Zap, MoreVertical, ShieldAlert, Ban, Check, CheckCheck } from "lucide-react";

const CHAT_PLANS = ["basic_199", "premium_399", "elite_699"];

const REPORT_REASONS = [
  { value: "spam", label: "Spam or promotional" },
  { value: "harassment", label: "Harassment or abuse" },
  { value: "fake_profile", label: "Fake or impersonation" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "scam", label: "Scam / asking for money" },
  { value: "other", label: "Something else" },
];

export default function Chat() {
  const { user } = useAuth();
  const { otherId } = useParams();
  const [threads, setThreads] = useState([]);
  const [messages, setMessages] = useState([]);
  const [other, setOther] = useState(null);
  const [text, setText] = useState("");
  const [locked, setLocked] = useState(null); // 'plan' | 'mutual' | 'blocked_by_me' | 'blocked_by_them'
  const [wsReady, setWsReady] = useState(false);
  const [otherTyping, setOtherTyping] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportReason, setReportReason] = useState("harassment");
  const [reportNote, setReportNote] = useState("");
  const [reporting, setReporting] = useState(false);

  const scrollRef = useRef(null);
  const wsRef = useRef(null);
  const typingTimerRef = useRef(null);
  const otherTypingTimerRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const readSentAtRef = useRef(0);

  const canChat = CHAT_PLANS.includes(user?.plan);

  const loadThreads = useCallback(() => {
    api.get("/chat/threads").then((r) => setThreads(r.data.threads)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!canChat) { setLocked("plan"); return; }
    loadThreads();
  }, [canChat, loadThreads]);

  const markRead = useCallback(() => {
    if (!otherId || !canChat) return;
    // avoid spamming: at most once every 800ms
    const now = Date.now();
    if (now - readSentAtRef.current < 800) return;
    readSentAtRef.current = now;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ type: "read" })); } catch { /* noop */ }
    } else {
      api.post(`/chat/${otherId}/read`).catch(() => {});
    }
  }, [otherId, canChat]);

  // History fetch + WebSocket connection
  useEffect(() => {
    if (!canChat || !otherId) return;
    let cancelled = false;
    let localWs = null;
    setLocked(null);
    setWsReady(false);
    setMessages([]);
    setOtherTyping(false);

    (async () => {
      try {
        const rel = await api.get(`/users/${otherId}/relation`);
        if (cancelled) return;
        if (rel.data.blocked_by_me) { setLocked("blocked_by_me"); return; }
        if (rel.data.blocked_by_them) { setLocked("blocked_by_them"); return; }
      } catch { /* noop */ }

      try {
        const r = await api.get(`/chat/${otherId}/messages`);
        if (cancelled) return;
        setMessages(r.data.messages);
      } catch (e) {
        if (cancelled) return;
        if (e.response?.status === 403) { setLocked("mutual"); return; }
        if (e.response?.status === 402) { setLocked("plan"); return; }
      }
      try {
        const p = await api.get(`/profiles/${otherId}`);
        if (!cancelled) setOther(p.data);
      } catch { /* noop */ }

      if (cancelled) return;
      const token = localStorage.getItem("uknri_token");
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${otherId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      if (cancelled) { try { ws.close(); } catch { /* noop */ } return; }
      localWs = ws;
      wsRef.current = ws;
      ws.onopen = () => { if (!cancelled) { setWsReady(true); markRead(); } };
      ws.onclose = () => { if (!cancelled) setWsReady(false); };
      ws.onerror = () => { if (!cancelled) setWsReady(false); };
      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(ev.data);
          if (data.message) {
            setMessages((prev) => (prev.some((m) => m.id === data.message.id) ? prev : [...prev, data.message]));
            if (data.message.from !== user?.id && document.visibilityState === "visible") {
              setTimeout(markRead, 150);
            }
          } else if (data.type === "typing" && data.by === otherId) {
            setOtherTyping(true);
            if (otherTypingTimerRef.current) clearTimeout(otherTypingTimerRef.current);
            otherTypingTimerRef.current = setTimeout(() => setOtherTyping(false), 3500);
          } else if (data.type === "read" && data.by === otherId) {
            const at = data.at;
            setMessages((prev) => prev.map((m) => (m.from === user?.id && !m.read_at ? { ...m, read_at: at } : m)));
          }
        } catch { /* noop */ }
      };
    })();

    return () => {
      cancelled = true;
      try { localWs?.close(); } catch { /* noop */ }
      if (typingTimerRef.current) clearTimeout(typingTimerRef.current);
      if (otherTypingTimerRef.current) clearTimeout(otherTypingTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [otherId, canChat]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
    // any time we render new messages, if the newest is inbound, mark read
    const last = messages[messages.length - 1];
    if (last && last.from !== user?.id && !last.read_at && document.visibilityState === "visible") {
      markRead();
    }
  }, [messages, markRead, user?.id]);

  const sendTyping = () => {
    const now = Date.now();
    if (now - lastTypingSentRef.current < 1800) return; // throttle
    lastTypingSentRef.current = now;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      try { wsRef.current.send(JSON.stringify({ type: "typing" })); } catch { /* noop */ }
    }
  };

  const send = async (e) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ text: t }));
      setText("");
    } else {
      try {
        const r = await api.post(`/chat/${otherId}/send`, { text: t });
        setMessages((prev) => [...prev, r.data.message]);
        setText("");
      } catch (err) { toast.error(formatApiError(err)); }
    }
  };

  const block = async () => {
    if (!otherId) return;
    if (!window.confirm(`Block ${other?.full_name || "this user"}? You will no longer see messages from them and they cannot contact you.`)) return;
    try {
      await api.post(`/users/${otherId}/block`);
      toast.success("User blocked");
      setLocked("blocked_by_me");
      try { wsRef.current?.close(); } catch { /* noop */ }
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const unblock = async () => {
    try {
      await api.delete(`/users/${otherId}/block`);
      toast.success("Unblocked");
      setLocked(null);
      // trigger a full reload of the conversation by tweaking a dep via a re-navigation loop.
      // Simplest: just refetch messages and re-open WS by resetting state.
      setMessages([]);
      const r = await api.get(`/chat/${otherId}/messages`);
      setMessages(r.data.messages);
      const token = localStorage.getItem("uknri_token");
      const wsUrl = BACKEND_URL.replace(/^http/, "ws") + `/api/ws/chat/${otherId}?token=${encodeURIComponent(token)}`;
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;
      ws.onopen = () => { setWsReady(true); markRead(); };
      ws.onclose = () => setWsReady(false);
      ws.onerror = () => setWsReady(false);
      ws.onmessage = (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (data.message) setMessages((prev) => [...prev, data.message]);
        } catch { /* noop */ }
      };
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const submitReport = async () => {
    setReporting(true);
    try {
      await api.post(`/users/${otherId}/report`, { reason: reportReason, note: reportNote });
      toast.success("Thanks — our team will review this report.");
      setReportOpen(false);
      setReportNote("");
      setReportReason("harassment");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setReporting(false);
    }
  };

  const headerMenu = useMemo(() => otherId ? (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button data-testid="chat-header-menu" variant="ghost" className="px-2 h-9 hover:bg-[#FAF6F0]"><MoreVertical className="w-4 h-4" /></Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem data-testid="chat-report-btn" onClick={() => setReportOpen(true)}>
          <ShieldAlert className="w-4 h-4 mr-2 text-amber-600" /> Report user
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        {locked === "blocked_by_me" ? (
          <DropdownMenuItem data-testid="chat-unblock-btn" onClick={unblock}>
            <Ban className="w-4 h-4 mr-2 text-emerald-700" /> Unblock user
          </DropdownMenuItem>
        ) : (
          <DropdownMenuItem data-testid="chat-block-btn" onClick={block} className="text-[#6B1D2F]">
            <Ban className="w-4 h-4 mr-2" /> Block user
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  ) : null, [otherId, locked]); // eslint-disable-line react-hooks/exhaustive-deps

  if (locked === "plan") {
    return (
      <div className="max-w-2xl mx-auto px-4 py-16">
        <Card className="p-10 text-center border-2 border-[#D4AF37] plan-card-featured" data-testid="chat-upgrade-prompt">
          <Lock className="w-12 h-12 mx-auto text-[#6B1D2F]" />
          <h2 className="mt-4 font-serif text-2xl text-slate-900">Chat is a paid feature</h2>
          <p className="text-slate-600 mt-2">Upgrade to <b>£199 Connect</b>, <b>£399 Verified</b>, or <b>£699 Elite</b> to start conversations with your mutual matches. Free and India Resident (£79) plans can browse, shortlist and express interest but cannot chat.</p>
          <Link to="/pricing"><Button data-testid="chat-see-plans-btn" className="mt-6 bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full px-8">See plans</Button></Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
      <aside className="lg:col-span-1">
        <Card className="border-[#EAE3D2] bg-white overflow-hidden">
          <div className="p-4 border-b border-[#EAE3D2] flex items-center justify-between">
            <h3 className="font-serif text-lg text-slate-900">Conversations</h3>
            {wsReady && <span className="flex items-center gap-1 text-xs text-emerald-700"><Zap className="w-3 h-3" /> live</span>}
          </div>
          <div className="divide-y divide-[#EAE3D2] max-h-[70vh] overflow-y-auto">
            {threads.length === 0 && <p className="p-5 text-sm text-slate-500">No mutual matches yet. Chat unlocks once both sides accept.</p>}
            {threads.map((t) => (
              <Link key={t.profile.id} to={`/chat/${t.profile.id}`} data-testid={`thread-${t.profile.id}`} className={`flex items-center gap-3 p-4 hover:bg-[#FAF6F0] ${otherId === t.profile.id ? "bg-[#FAF6F0]" : ""} ${t.partner_invalid ? "opacity-60" : ""}`}>
                <div className="w-10 h-10 rounded-full bg-[#6B1D2F] text-white flex items-center justify-center font-serif overflow-hidden">
                  {t.profile.profile_photo_path ? <img src={fileUrl(t.profile.profile_photo_path)} alt="" className={`w-full h-full object-cover ${t.partner_invalid ? "grayscale" : ""}`} /> : t.profile.full_name?.[0]}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-semibold text-slate-900 truncate flex-1">{t.profile.full_name}</p>
                    {t.unread > 0 && !t.partner_invalid && <span data-testid={`thread-unread-${t.profile.id}`} className="bg-[#6B1D2F] text-white text-[10px] rounded-full min-w-[18px] h-[18px] px-1.5 flex items-center justify-center font-bold">{t.unread}</span>}
                  </div>
                  <p className="text-xs text-slate-500 truncate">
                    {t.partner_invalid ? <span className="text-[#6B1D2F] italic">This profile is no longer available</span> : (t.last_message || "Say hello 👋")}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </Card>
      </aside>

      <main className="lg:col-span-2">
        <Card className="border-[#EAE3D2] bg-white flex flex-col h-[70vh]">
          {!otherId ? (
            <div className="flex-1 flex items-center justify-center text-slate-500 flex-col gap-3">
              <MessageCircle className="w-12 h-12 text-[#6B1D2F]/40" />
              <p>Select a conversation to start chatting.</p>
            </div>
          ) : locked === "mutual" ? (
            <div className="flex-1 flex items-center justify-center text-slate-600 p-6 text-center flex-col gap-3">
              <Lock className="w-10 h-10 text-[#6B1D2F]/60" />
              <p>Chat unlocks after both sides accept the expression of interest.</p>
              <Link to="/matches"><Button variant="outline" className="rounded-full">Manage interests</Button></Link>
            </div>
          ) : locked === "blocked_by_me" ? (
            <>
              <div className="p-4 border-b border-[#EAE3D2] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-slate-300 text-white flex items-center justify-center font-serif overflow-hidden">
                    {other?.full_name?.[0] || "?"}
                  </div>
                  <div>
                    <p className="font-serif text-lg text-slate-900">{other?.full_name || "This user"}</p>
                    <p className="text-xs text-[#6B1D2F]">You blocked this user</p>
                  </div>
                </div>
                {headerMenu}
              </div>
              <div className="flex-1 flex items-center justify-center text-slate-600 p-6 text-center flex-col gap-3" data-testid="chat-blocked-by-me">
                <Ban className="w-10 h-10 text-[#6B1D2F]" />
                <p>You've blocked this user. Messages are hidden and they cannot contact you.</p>
                <Button data-testid="chat-unblock-inline-btn" onClick={unblock} variant="outline" className="rounded-full">Unblock</Button>
              </div>
            </>
          ) : locked === "blocked_by_them" ? (
            <div className="flex-1 flex items-center justify-center text-slate-600 p-6 text-center flex-col gap-3" data-testid="chat-blocked-by-them">
              <Ban className="w-10 h-10 text-slate-400" />
              <p>This conversation is no longer available.</p>
              <Link to="/matches"><Button variant="outline" className="rounded-full">Back to matches</Button></Link>
            </div>
          ) : (
            <>
              {other && (
                <div className="p-4 border-b border-[#EAE3D2] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#6B1D2F] text-white flex items-center justify-center font-serif overflow-hidden">
                      {other.profile_photo_path ? <img src={fileUrl(other.profile_photo_path)} alt="" className="w-full h-full object-cover" /> : other.full_name?.[0]}
                    </div>
                    <div>
                      <p className="font-serif text-lg text-slate-900">{other.full_name}</p>
                      <p className="text-xs text-slate-500">{other.profession} · {other.current_city}</p>
                    </div>
                  </div>
                  {headerMenu}
                </div>
              )}
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-2 bg-[#FDFBF7]">
                {messages.map((m) => {
                  const mine = m.from === user?.id;
                  return (
                    <div key={m.id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[75%] rounded-2xl px-4 py-2 text-sm ${mine ? "bg-[#6B1D2F] text-white" : "bg-white border border-[#EAE3D2] text-slate-800"}`}>
                        <div>{m.text}</div>
                        {mine && (
                          <div className="flex justify-end mt-1" data-testid={`msg-ticks-${m.id}`}>
                            {m.read_at ? (
                              <CheckCheck className="w-3.5 h-3.5 text-[#D4AF37]" />
                            ) : (
                              <Check className="w-3.5 h-3.5 text-white/70" />
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
                {messages.length === 0 && <p className="text-center text-sm text-slate-500 mt-8">No messages yet — say hello 👋</p>}
                {otherTyping && (
                  <div className="flex justify-start" data-testid="chat-typing-indicator">
                    <div className="bg-white border border-[#EAE3D2] rounded-2xl px-3 py-2 text-slate-500 text-sm inline-flex items-center gap-1">
                      <span className="typing-dot animate-bounce" style={{ animationDelay: "0ms" }}>●</span>
                      <span className="typing-dot animate-bounce" style={{ animationDelay: "150ms" }}>●</span>
                      <span className="typing-dot animate-bounce" style={{ animationDelay: "300ms" }}>●</span>
                    </div>
                  </div>
                )}
              </div>
              <form onSubmit={send} className="p-3 border-t border-[#EAE3D2] flex gap-2">
                <Input
                  value={text}
                  onChange={(e) => { setText(e.target.value); if (e.target.value.trim()) sendTyping(); }}
                  placeholder="Type a message…"
                  data-testid="chat-input"
                />
                <Button type="submit" data-testid="chat-send-btn" className="bg-[#6B1D2F] hover:bg-[#541624] text-white rounded-full"><Send className="w-4 h-4" /></Button>
              </form>
            </>
          )}
        </Card>
      </main>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent data-testid="report-dialog">
          <DialogHeader>
            <DialogTitle>Report {other?.full_name || "user"}</DialogTitle>
            <DialogDescription>Our team reviews every report. Blocking someone is instant — reporting helps us keep the community safe.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-1 gap-2">
              {REPORT_REASONS.map((r) => (
                <label key={r.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer ${reportReason === r.value ? "border-[#6B1D2F] bg-[#6B1D2F]/5" : "border-[#EAE3D2]"}`}>
                  <input
                    type="radio"
                    name="report-reason"
                    value={r.value}
                    checked={reportReason === r.value}
                    onChange={() => setReportReason(r.value)}
                    data-testid={`report-reason-${r.value}`}
                  />
                  <span className="text-sm text-slate-800">{r.label}</span>
                </label>
              ))}
            </div>
            <Textarea
              value={reportNote}
              onChange={(e) => setReportNote(e.target.value)}
              placeholder="Add details (optional)…"
              rows={3}
              data-testid="report-note"
              maxLength={1000}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReportOpen(false)} data-testid="report-cancel-btn">Cancel</Button>
            <Button
              onClick={submitReport}
              disabled={reporting}
              data-testid="report-submit-btn"
              className="bg-[#6B1D2F] hover:bg-[#541624] text-white"
            >
              {reporting ? "Sending…" : "Submit report"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
